import { Skeleton } from '@/components/ui/skeleton';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const SIDEBAR_W = 240;
const TOPBAR_H = 56;

/** Skeleton that mimics the AppShell layout — used while session is loading or a page chunk is lazy-loading */
export function AppShellSkeleton(): JSX.Element {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';

  return (
    <div style={{ background: 'var(--color-bg)', minHeight: '100vh' }}>

      {/* Sidebar — hidden on mobile */}
      {!isMobile && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, bottom: 0,
          width: SIDEBAR_W,
          background: 'var(--color-surface)',
          borderRight: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 12px',
          gap: 4,
          zIndex: 40,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', marginBottom: 16 }}>
            <Skeleton style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
            <Skeleton style={{ width: 80, height: 14, borderRadius: 6 }} />
          </div>

          {[100, 84, 92, 88, 76, 96].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8 }}>
              <Skeleton style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0 }} />
              <Skeleton style={{ width: w, height: 12, borderRadius: 5 }} />
            </div>
          ))}

          <div style={{ height: 1, background: 'var(--color-border)', margin: '8px 0' }} />

          {[88, 100, 80, 94, 72].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8 }}>
              <Skeleton style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0 }} />
              <Skeleton style={{ width: w, height: 12, borderRadius: 5 }} />
            </div>
          ))}

          <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px' }}>
            <Skeleton style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
              <Skeleton style={{ width: '60%', height: 11, borderRadius: 5 }} />
              <Skeleton style={{ width: '80%', height: 10, borderRadius: 5 }} />
            </div>
          </div>
        </div>
      )}

      {/* Topbar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : SIDEBAR_W,
        right: 0,
        height: TOPBAR_H,
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '0 16px' : '0 28px',
        zIndex: 30,
      }}>
        <Skeleton style={{ width: 160, height: 14, borderRadius: 6 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!isMobile && <Skeleton style={{ width: 32, height: 32, borderRadius: 8 }} />}
          <Skeleton style={{ width: 32, height: 32, borderRadius: '50%' }} />
        </div>
      </div>

      {/* Page content */}
      <main style={{ marginLeft: isMobile ? 0 : SIDEBAR_W, paddingTop: TOPBAR_H }}>
        <div style={{ padding: isMobile ? '20px 16px' : '28px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Page heading */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Skeleton style={{ width: 200, height: 22, borderRadius: 7 }} />
              <Skeleton style={{ width: 130, height: 13, borderRadius: 5 }} />
            </div>
            {!isMobile && <Skeleton style={{ width: 120, height: 36, borderRadius: 8 }} />}
          </div>

          {/* Stat cards row */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ background: 'var(--color-surface)', borderRadius: 12, border: '1px solid var(--color-border)', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Skeleton style={{ width: 90, height: 11, borderRadius: 5 }} />
                  <Skeleton style={{ width: 28, height: 28, borderRadius: 7 }} />
                </div>
                <Skeleton style={{ width: 56, height: 26, borderRadius: 6 }} />
              </div>
            ))}
          </div>

          {/* Table / content block */}
          <div style={{ background: 'var(--color-surface)', borderRadius: 12, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Skeleton style={{ width: 180, height: 13, borderRadius: 5 }} />
              {!isMobile && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <Skeleton style={{ width: 100, height: 32, borderRadius: 7 }} />
                  <Skeleton style={{ width: 80, height: 32, borderRadius: 7 }} />
                </div>
              )}
            </div>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 14 }}>
                <Skeleton style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                  <Skeleton style={{ width: `${[40, 55, 35, 48, 42][i]}%`, height: 12, borderRadius: 5 }} />
                  <Skeleton style={{ width: `${[28, 32, 25, 36, 30][i]}%`, height: 10, borderRadius: 5 }} />
                </div>
                {!isMobile && <Skeleton style={{ width: 64, height: 22, borderRadius: 20 }} />}
                {!isMobile && <Skeleton style={{ width: 72, height: 11, borderRadius: 5 }} />}
              </div>
            ))}
          </div>

        </div>
      </main>
    </div>
  );
}
